import React, { useCallback, useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import Logo from "@ciq/shared/components/logo"
import Api from "@ciq/shared/api/api"
import { Banner } from "@ciq/shared/components/views"
import { ContactAdminModal } from "@ciq/shared/components/modal"
import { useAppContext } from "@ciq/shared/hooks/context"
import { LoadingStatus } from "@ciq/shared/api/loading"
import { useQuickSightEmbed } from "@ciq/shared/hooks/use_quicksight_embed"

const LOADING_ID = "console:load"

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Extract a dashboard ID from a PAGE_NAVIGATION message.
 * Checks undocumented runtime fields (dashboardId, pageId, id) and
 * validates the result as a UUID. Returns null if no valid UUID found.
 */
export function extractDashboardId(message) {
  const candidate = message.dashboardId || message.pageId || message.id || null
  return candidate && UUID_RE.test(candidate) ? candidate : null
}

export function Console({ onContactClick }) {
  const {
    schoolId,
    user,
    views,
    landingViewLoadingState,
    addLoadingId,
    removeLoadingId,
  } = useAppContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const dashboardParam = searchParams.get("dashboard") || null

  const [url, setUrl] = useState(null)
  const [error, setError] = useState(null)
  const [viewRequestModal, setViewRequestModal] = useState(false)

  const addLoadingIdRef = useRef(addLoadingId)
  const removeLoadingIdRef = useRef(removeLoadingId)
  const containerRef = useRef(null)
  const isInternalNavRef = useRef(false)
  const dashboardParamRef = useRef(dashboardParam)
  const hasLoadedRef = useRef(false)
  const searchParamsRef = useRef(searchParams)
  searchParamsRef.current = searchParams

  const fetchAndSetUrl = useCallback((dashboardId) => {
    addLoadingIdRef.current(LOADING_ID)
    const controller = new AbortController()
    let apiUrl = `/console/${schoolId}`
    if (dashboardId) {
      const params = new URLSearchParams({ dashboard: dashboardId })
      for (const [key, value] of searchParamsRef.current.entries()) {
        if (key !== "dashboard" && value) {
          params.set(key, value)
        }
      }
      apiUrl = `${apiUrl}?${params.toString()}`
    }
    Api.get(apiUrl, { signal: controller.signal })
      .then(response => {
        setUrl(response.data.url)
        setError(null)
      })
      .catch(e => {
        if (e.error?.code === "ERR_CANCELED") return
        setError("Unable to load dashboard")
        if (e.handled) return
        console.error(e.error)
      })
      .finally(() => {
        removeLoadingIdRef.current(LOADING_ID)
      })
    return () => controller.abort()
  }, [schoolId])

  useEffect(() => {
    if (
      schoolId == null ||
      !views.can_view_dashboard ||
      landingViewLoadingState !== LoadingStatus.FETCHED
    ) {
      return
    }

    if (process.env.REACT_APP_QUICKSIGHT_DEV_MOCK === "true") {
      setUrl("dev-mock://placeholder")
      return
    }

    return fetchAndSetUrl(dashboardParamRef.current)
  }, [schoolId, fetchAndSetUrl, views.can_view_dashboard, landingViewLoadingState])

  // Track when initial load completes
  useEffect(() => {
    if (url !== null) hasLoadedRef.current = true
  }, [url])

  // Handle SDK navigation events → update URL
  const handleNavigate = useCallback(
    (message) => {
      const dashboardId = extractDashboardId(message)
      if (!dashboardId) {
        console.warn(
          "PAGE_NAVIGATION event for dashboard page type contained no extractable dashboard ID.",
          "URL sync skipped. Event payload:",
          message
        )
        return
      }
      if (dashboardId === dashboardParamRef.current) return

      isInternalNavRef.current = true
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.set("dashboard", dashboardId)
        return next
      })
    },
    [setSearchParams]
  )

  // Handle browser back/forward → re-fetch embed URL
  useEffect(() => {
    const prevDashboard = dashboardParamRef.current
    dashboardParamRef.current = dashboardParam

    if (isInternalNavRef.current) {
      isInternalNavRef.current = false
      return
    }

    if (dashboardParam !== prevDashboard && hasLoadedRef.current) {
      return fetchAndSetUrl(dashboardParam)
    }
  }, [dashboardParam, schoolId, fetchAndSetUrl])

  // SDK embedding
  const { error: sdkError, isLoaded } = useQuickSightEmbed({
    containerRef,
    url,
    onNavigate: handleNavigate,
  })

  const handleOnContact = useCallback(
    message => {
      if (!onContactClick) {
        hideContactModal()
        return
      }

      const loadingId = "console:message"
      addLoadingIdRef.current(loadingId)
      onContactClick(message, successful => {
        removeLoadingIdRef.current(loadingId)
        if (successful) {
          hideContactModal()
        }
      })
    },
    [onContactClick]
  )

  if (landingViewLoadingState !== LoadingStatus.FETCHED) {
    return null
  }

  const showContactModal = () => setViewRequestModal(true)
  const hideContactModal = () => setViewRequestModal(false)

  if (!views.can_view_dashboard) {
    return (
      <div className="da-quicksight-request-rights">
        <div className="da-quicksight-request-rights-banner">
          <Banner>
            You do not have the permissions to view this page.{" "}
            <span onClick={showContactModal}>Contact Admin</span> to request view rights.
          </Banner>
        </div>
        <div className="da-quicksight-request-rights-container">
          <Logo />
          <div className="da-card">
            <h1>Request Access</h1>
            <div>
              <span>You do not have permissions to view this page</span>
            </div>
          </div>
        </div>
        <ContactAdminModal
          show={viewRequestModal}
          title="Request Access"
          placeholderOptions={{
            message: `Hi! This is ${user.name} and I'm requesting permissions to be a Dashboard viewer`,
          }}
          onHide={hideContactModal}
          onContact={handleOnContact}
        />
      </div>
    )
  }

  if (error || sdkError) {
    return (
      <div className="error-container">
        <div>
          <span>{error || sdkError}</span>
        </div>
      </div>
    )
  }

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {url && !isLoaded && (
        <div
          className="console-loading"
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
          }}
        >
          <span>Loading Dashboard...</span>
        </div>
      )}
      <div
        ref={containerRef}
        className="da-quicksight-console"
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  )
}
